import type { OnboardingTask } from 'src/entities/OnboardingStatus';
import { TaskCodes } from 'src/legacy/components/HomePage/OnboardingTasksSection/constants';

/**
 * The two Studio FTUX onboarding tasks shown in the sidebar "Getting started"
 * card. Order here is the order they render in.
 */
export const STUDIO_CHECKLIST_TASK_IDS = [
  'publishApp',
  'explorePortal',
] as const;

export type StudioChecklistTaskId = (typeof STUDIO_CHECKLIST_TASK_IDS)[number];

/**
 * Progress credited the moment the card first appears. By the time a user lands
 * on this checklist they've already clicked through the Studio onboarding
 * screens, so starting the bar at zero understates how far along they are; the
 * task completions fill the remaining range on top of this baseline.
 */
export const STUDIO_CHECKLIST_BASELINE_PCT = 20;

/** A full bar — every task done. */
export const STUDIO_CHECKLIST_COMPLETE_PCT = 100;

/** The task that advances on click and, once done, hides the whole checklist. */
export const EXPLORE_PORTAL_TASK_ID =
  'explorePortal' satisfies StudioChecklistTaskId;

/**
 * Task status, matching the values the backend reports. `InProgress` renders
 * the half-filled mark; `Done` is the terminal/complete state.
 */
export enum StudioChecklistTaskStatus {
  Todo = 'todo',
  InProgress = 'in-progress',
  Done = 'done',
}

export interface StudioChecklistTask {
  id: StudioChecklistTaskId;
  label: string;
  status: StudioChecklistTaskStatus;
}

export const GUIDED_TOUR_PARAM = 'guided-tour';
export const GUIDED_TOUR_FIRST_CLIENT = 'firstClient';

/** Every checklist task's status — the input both progress bars are derived from. */
export type StudioChecklistTaskStatuses = Record<
  StudioChecklistTaskId,
  StudioChecklistTaskStatus
>;

/** Maps each checklist task to the backend onboarding task code it reflects. */
export const STUDIO_CHECKLIST_TASK_CODE_BY_ID: Record<
  StudioChecklistTaskId,
  TaskCodes
> = {
  publishApp: TaskCodes.TaskCodeFirstApp,
  explorePortal: TaskCodes.TaskCodeFirstClient,
};

// The backend status strings and the checklist enum share the same values; map
// explicitly so the enum-typed UI stays type-safe. `done` is checked first
// because a recorded completion is terminal server-side, and `status` is still
// optional on the generated type — a task carrying only `done` must not read
// as Todo.
const toChecklistStatus = (
  task: OnboardingTask | undefined,
): StudioChecklistTaskStatus => {
  if (task?.done) {
    return StudioChecklistTaskStatus.Done;
  }
  switch (task?.status) {
    case 'in-progress':
      return StudioChecklistTaskStatus.InProgress;
    case 'done':
      return StudioChecklistTaskStatus.Done;
    default:
      return StudioChecklistTaskStatus.Todo;
  }
};

/** Reads the checklist's state off the backend onboarding task list. */
export const getStudioChecklistTaskStatuses = (
  tasks: OnboardingTask[] | undefined,
): StudioChecklistTaskStatuses => {
  const taskByCode = new Map(
    (tasks ?? []).map((task) => [task.taskCode, task]),
  );
  // Spelled out rather than built from the id list so a new task can't silently
  // go unmapped — the record type makes the compiler ask for it here.
  return {
    publishApp: toChecklistStatus(
      taskByCode.get(STUDIO_CHECKLIST_TASK_CODE_BY_ID.publishApp),
    ),
    explorePortal: toChecklistStatus(
      taskByCode.get(STUDIO_CHECKLIST_TASK_CODE_BY_ID.explorePortal),
    ),
  };
};

/** What one completed task adds: the post-baseline range split across the tasks. */
const TASK_STEP_PCT =
  (STUDIO_CHECKLIST_COMPLETE_PCT - STUDIO_CHECKLIST_BASELINE_PCT) /
  STUDIO_CHECKLIST_TASK_IDS.length;

/** A started task is credited half a step, so the bar moves the moment work begins. */
const TASK_HALF_STEP_PCT = TASK_STEP_PCT / 2;

/**
 * The percentage the "Getting started" bar fills to. Tasks fill on top of the
 * baseline, so the bar reads as already underway on arrival (the user has been
 * through the onboarding screens) and still reaches 100% once all tasks are done.
 */
export const getStudioChecklistCompletionPct = (
  statuses: StudioChecklistTaskStatuses,
): number => {
  const earned = STUDIO_CHECKLIST_TASK_IDS.reduce((total, id) => {
    switch (statuses[id]) {
      case StudioChecklistTaskStatus.Done:
        return total + TASK_STEP_PCT;
      case StudioChecklistTaskStatus.InProgress:
        return total + TASK_HALF_STEP_PCT;
      default:
        return total;
    }
  }, 0);
  return Math.round(STUDIO_CHECKLIST_BASELINE_PCT + earned);
};

/**
 * The percentage the client-preview card shows. Opening the preview *is* the
 * explore-your-portal step in flight, so the client bar sits half a step ahead of
 * the dashboard bar the user just left — the two stay on one scale instead of the
 * preview showing an unrelated number.
 */
export const getStudioClientPreviewProgressPct = (
  statuses: StudioChecklistTaskStatuses,
): number =>
  Math.min(
    getStudioChecklistCompletionPct(statuses) + TASK_HALF_STEP_PCT,
    STUDIO_CHECKLIST_COMPLETE_PCT,
  );
